import type { ETSStub } from "../auto-init.js";

const PUBLIC_METHODS = [
  "page",
  "track",
  "identify",
  "group",
  "alias",
  "flush",
  "ready",
  "reset",
] as const;

export interface BootstrapInput {
  writeKey: string;
  scriptSrc: string;
}

export function bootstrapLoader(input: BootstrapInput): ETSStub {
  const existing = window.ets;
  if (existing && "_q" in existing && Array.isArray(existing._q)) return existing as ETSStub;

  const stub: ETSStub = {
    _q: [],
    _k: input.writeKey,
    _h: "",
    _src: input.scriptSrc,
    _t: {
      t: performance.now(),
      o: performance.timeOrigin,
      r: document.referrer,
      u: location.href,
    },
  };
  for (const m of PUBLIC_METHODS) {
    stub[m] = (...args: unknown[]) => {
      stub._q.push([m, args]);
    };
  }
  window.ets = stub;
  return stub;
}

export function parseWriteKeyFromScript(src: string): string | null {
  try {
    const u = new URL(src);
    const k = u.searchParams.get("key");
    return k && k.length > 0 ? k : null;
  } catch {
    return null;
  }
}

export function findOwnScriptSrc(): string | null {
  // iterate newest-first: the loader is typically the last injected script
  const cur = document.currentScript;
  if (cur instanceof HTMLScriptElement && cur.src) return cur.src;
  const scripts = document.getElementsByTagName("script");
  for (let i = scripts.length - 1; i >= 0; i--) {
    const s = scripts[i];
    if (s instanceof HTMLScriptElement && s.src && /\/loader\.js(\?|$)/.test(s.src)) {
      return s.src;
    }
  }
  return null;
}

export interface BundleEntry {
  url: string;
  integrity: string;
}

export interface ModuleEntry {
  name: string;
  url: string;
  integrity: string;
}

export type BundleVariant = "lite" | "core" | "full";

export interface RemoteConfig {
  endpoint: string;
  variant: BundleVariant;
  legacyMode: boolean;
  modules: ModuleEntry[];
  bundles: Partial<Record<BundleVariant, BundleEntry>>;
}

export interface LoadInput {
  writeKey: string;
  configBaseUrl: string;
  fetchFn?: typeof fetch;
}

function injectScript(opts: {
  src: string;
  integrity: string;
  attrs: Record<string, string>;
}): void {
  const s = document.createElement("script");
  s.src = opts.src;
  if (opts.integrity) s.integrity = opts.integrity;
  s.crossOrigin = "anonymous";
  s.async = true;
  for (const [k, v] of Object.entries(opts.attrs)) s.setAttribute(k, v);
  (document.head || document.documentElement).appendChild(s);
}

export async function loadConfigAndInject(input: LoadInput): Promise<void> {
  const f = input.fetchFn ?? fetch;
  const url = `${input.configBaseUrl}/v1/c/${encodeURIComponent(input.writeKey)}.json`;
  let config: RemoteConfig;
  try {
    const res = await f(url, { credentials: "omit", cache: "default" });
    if (!res.ok) {
      // biome-ignore lint/suspicious/noConsole: loader config fetch failed — SDK will not initialize
      console.warn(`[ets] config fetch failed (${res.status}) — SDK will not load`);
      return;
    }
    config = (await res.json()) as RemoteConfig;
  } catch (err) {
    // biome-ignore lint/suspicious/noConsole: loader config fetch error — SDK will not initialize
    console.warn("[ets] config fetch error — SDK will not load", err);
    return;
  }

  const bundle = config.bundles[config.variant];
  if (!bundle) return;

  (
    window as Window & {
      __ETS_CONFIG__?: { endpoint: string; writeKey: string; legacyMode: boolean };
    }
  ).__ETS_CONFIG__ = {
    endpoint: config.endpoint,
    writeKey: input.writeKey,
    legacyMode: config.legacyMode,
  };

  injectScript({
    src: bundle.url,
    integrity: bundle.integrity,
    attrs: { "data-ets-bundle": config.variant },
  });

  for (const m of config.modules) {
    injectScript({
      src: m.url,
      integrity: m.integrity,
      attrs: { "data-ets-module": m.name },
    });
  }
}
